import { ApiClient } from '@api/api-configuration';
import { CaregiverMinified } from '@api/READ_ONLY/caregiver_api/Api';
import { Client } from '@api/READ_ONLY/client_api/Api';
import { Doctor } from '@api/READ_ONLY/doctors_api/Api';
import CaregiversSelectGeneric from '@app/features/caregivers/components/CaregiversSelect';
import ClientSelectGeneric from '@app/features/clients/components/ClientSelect';
import DoctorsSelectGeneric from '@app/features/doctors/components/DoctorsSelect';
import { FieldLabel } from '@app/features/form/Components/FieldLabel';
import { FieldComponentProps } from '@app/types/form.types';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const CaregiverSelectField: React.FC<FieldComponentProps<number | undefined>> = ({ value, onChange, error }) => {
  const { t } = useTranslation();
  const [caregiver, setCaregiver] = useState<CaregiverMinified | undefined>();
  // While the prefilled caregiver's details are being fetched on first mount,
  // show a loading state instead of letting the Select flash the raw caregiver id.
  // Gated by a ref so it only applies to the initial resolve, never to user picks
  // (those already have a label available from the dropdown).
  const [resolvingInitial, setResolvingInitial] = useState<boolean>(() => !!value);
  const didInitialResolve = useRef(false);

  useEffect(() => {
    const finishInitial = () => {
      if (!didInitialResolve.current) {
        didInitialResolve.current = true;
        setResolvingInitial(false);
      }
    };

    if (value) {
      // Fetch caregiver to display in the dropdown
      ApiClient.caregiverApi.caregivers
        .getCaregiverById(value)
        .then((response) => {
          if (response.data) {
            const { caregiverId, firstName, lastName, email, city, dateOfBirth, phoneMobile, phoneHome } =
              response.data;
            setCaregiver({
              caregiverId,
              firstName,
              lastName,
              email,
              city,
              dateOfBirth,
              phoneMobile,
              phoneHome,
              street: response.data.street,
              postalCode: response.data.postalCode,
            });
          }
        })
        .catch((err) => {
          console.warn('Failed to fetch caregiver for dropdown:', err);
        })
        .finally(finishInitial);
    } else {
      setCaregiver(undefined);
      finishInitial();
    }
  }, [value]);

  // Don't render the field until the prefilled caregiver's name is ready, so the
  // label and dropdown appear together and it never flashes the raw caregiver id.
  if (resolvingInitial) {
    return null;
  }

  return (
    <>
      <FieldLabel label={t('users-form-caregiver')} required />
      <CaregiversSelectGeneric
        size="large"
        placeholder={t('users-select-caregiver-placeholder')}
        value={value}
        active={null}
        selectedItemsProvider={() => (caregiver ? caregiver : undefined)}
        onCaregiverChange={(caregiver: CaregiverMinified | CaregiverMinified[] | undefined) => {
          if (caregiver && !Array.isArray(caregiver)) {
            onChange(caregiver.caregiverId);
          } else {
            onChange(undefined);
          }
        }}
      />
      {error && <p className="text-app-danger text-xs mt-1">{t('users-validation-caregiver-required')}</p>}
    </>
  );
};

export const ClientSelectField: React.FC<FieldComponentProps<number | undefined>> = ({ value, onChange, error }) => {
  const { t } = useTranslation();
  const [client, setClient] = useState<Client | undefined>();
  // See CaregiverSelectField: avoid flashing the raw client id while the prefilled
  // client's details load on first mount.
  const [resolvingInitial, setResolvingInitial] = useState<boolean>(() => !!value);
  const didInitialResolve = useRef(false);

  useEffect(() => {
    const finishInitial = () => {
      if (!didInitialResolve.current) {
        didInitialResolve.current = true;
        setResolvingInitial(false);
      }
    };

    if (value) {
      // Fetch client to display in the dropdown
      ApiClient.clientApi.clients
        .getClientById(value)
        .then((response) => {
          if (response.data) {
            setClient(response.data);
          }
        })
        .catch((err) => {
          console.warn('Failed to fetch client for dropdown:', err);
        })
        .finally(finishInitial);
    } else {
      setClient(undefined);
      finishInitial();
    }
  }, [value]);

  // Don't render the field until the prefilled patient's name is ready, so the
  // label and dropdown appear together and it never flashes the raw client id.
  if (resolvingInitial) {
    return null;
  }

  return (
    <>
      <FieldLabel label={t('users-form-patient')} required />
      <ClientSelectGeneric
        size="large"
        placeholder={t('users-select-patient-placeholder')}
        value={value}
        selectedItemsProvider={() => (client ? client : undefined)}
        onClientChange={(client: Client | Client[] | undefined) => {
          if (client && !Array.isArray(client)) {
            onChange(client.clientId);
          } else {
            onChange(undefined);
          }
        }}
      />
      {error && <p className="text-app-danger text-xs mt-1">{t('users-validation-patient-required')}</p>}
    </>
  );
};

/**
 * The doctor an account is linked to.
 *
 * Plainer than the caregiver and client pickers on purpose. Those search the
 * server because there are thousands of each; doctors are a short list a
 * practice maintains by hand, so one fetch and a filterable Select is the whole
 * requirement — a search-as-you-type wrapper here would be machinery with
 * nothing to do.
 *
 * The value is `doctorId`, which is what makes the link work: the backend's
 * ROLE_ENTITY_CONFIG writes it to `doctors.user_id` and pushes it to Keycloak as
 * the `doctorId` attribute, and every doctor RLS policy matches on it.
 */
export const DoctorSelectField: React.FC<FieldComponentProps<number | undefined>> = ({ value, onChange, error }) => {
  const { t } = useTranslation();
  const [doctor, setDoctor] = useState<Doctor | undefined>();
  const [loadFailed, setLoadFailed] = useState(false);
  // See CaregiverSelectField: avoid flashing the raw doctor id while the prefilled
  // doctor's details load on first mount.
  const [resolvingInitial, setResolvingInitial] = useState<boolean>(() => !!value);
  const didInitialResolve = useRef(false);

  useEffect(() => {
    const finishInitial = () => {
      if (!didInitialResolve.current) {
        didInitialResolve.current = true;
        setResolvingInitial(false);
      }
    };

    if (value) {
      ApiClient.doctorsApi.doctors
        .getDoctorById(value)
        .then((response) => {
          if (response.data) setDoctor(response.data);
        })
        .catch((err) => {
          console.warn('Failed to fetch doctor for dropdown:', err);
        })
        .finally(finishInitial);
    } else {
      setDoctor(undefined);
      finishInitial();
    }
  }, [value]);

  if (resolvingInitial) {
    return null;
  }

  return (
    <>
      <FieldLabel label={t('users-form-doctor')} required />
      <DoctorsSelectGeneric
        size="large"
        placeholder={t('users-select-doctor-placeholder')}
        value={value}
        // Disabled as well as announced: the operator cannot fix a failed load by
        // choosing something, and an empty picker invites them to save without the link.
        // `undefined`, not false, so a surrounding read-only form still disables it.
        disabled={loadFailed || undefined}
        selectedItemsProvider={() => doctor}
        onFetchError={setLoadFailed}
        onDoctorChange={(picked: Doctor | Doctor[] | undefined) => {
          if (picked && !Array.isArray(picked)) {
            onChange(picked.doctorId);
          } else {
            onChange(undefined);
          }
        }}
      />
      {/* An empty required picker is indistinguishable from "this deployment has no
          doctors yet", so a failed fetch has to be said out loud: the operator cannot
          fix it by choosing something, and a user saved without the link holds a role
          that can never be scoped. */}
      {loadFailed && <p className="text-app-danger text-xs mt-1">{t('users-doctor-load-failed')}</p>}
      {error && !loadFailed && <p className="text-app-danger text-xs mt-1">{t('users-validation-doctor-required')}</p>}
    </>
  );
};
